import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Eye, BarChart2 } from "lucide-react";

const EMAIL_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending:   { label: "Pendente",   variant: "secondary" },
  sent:      { label: "Enviado",    variant: "default" },
  delivered: { label: "Entregue",   variant: "default" },
  opened:    { label: "Aberto",     variant: "default" },
  clicked:   { label: "Clicado",    variant: "default" },
  bounced:   { label: "Rejeitado",  variant: "destructive" },
  failed:    { label: "Falhou",     variant: "destructive" },
};

export function MessageDetailDialog({
  messageId,
  initialTab,
  onClose,
}: {
  messageId: number | null;
  initialTab: "details" | "report";
  onClose: () => void;
}) {
  const open = messageId !== null;
  const { data, isLoading } = trpc.sendgrid.history.getMessage.useQuery(
    { id: messageId ?? 0 },
    { enabled: open },
  );

  const message = data?.message;
  const events = data?.events ?? [];

  const eventLabel: Record<string, string> = {
    delivered: "Entregue",
    open: "Aberto",
    click: "Clicado",
    bounce: "Bounced",
    dropped: "Descartado",
    unsubscribe: "Descadastrado",
    spamreport: "Marcado como spam",
    deferred: "Adiado",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle className="truncate">
            {message?.subject ?? "Detalhes da mensagem"}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <p className="text-sm text-muted-foreground py-8 text-center">Carregando…</p>
        )}

        {!isLoading && !message && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Mensagem não encontrada.
          </p>
        )}

        {message && (
          <Tabs defaultValue={initialTab} className="mt-2">
            <TabsList>
              <TabsTrigger value="details" className="gap-2">
                <Eye className="h-4 w-4" /> Detalhes
              </TabsTrigger>
              <TabsTrigger value="report" className="gap-2">
                <BarChart2 className="h-4 w-4" /> Relatório
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Destinatário</Label>
                  <p className="font-mono text-xs mt-0.5 break-all">{message.to}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div className="mt-0.5">
                    <Badge variant={EMAIL_STATUS_CONFIG[message.status]?.variant ?? "outline"}>
                      {EMAIL_STATUS_CONFIG[message.status]?.label ?? message.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Enviado em</Label>
                  <p className="text-xs mt-0.5">
                    {message.sentAt
                      ? new Date(message.sentAt).toLocaleString("pt-BR")
                      : "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">SendGrid Message ID</Label>
                  <p className="font-mono text-xs mt-0.5 break-all">
                    {message.sendgridMessageId || "—"}
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-muted-foreground">Pré-visualização</Label>
                {message.html ? (
                  <iframe
                    title="email-preview"
                    srcDoc={message.html}
                    sandbox=""
                    className="mt-2 w-full h-[400px] border rounded-lg bg-white"
                  />
                ) : (
                  <p className="text-xs text-muted-foreground mt-2">
                    HTML não disponível.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="report" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Status atual</Label>
                  <div className="mt-0.5">
                    <Badge variant={EMAIL_STATUS_CONFIG[message.status]?.variant ?? "outline"}>
                      {EMAIL_STATUS_CONFIG[message.status]?.label ?? message.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total de eventos</Label>
                  <p className="text-sm font-medium mt-0.5">{events.length}</p>
                </div>
              </div>

              <Separator />

              <div>
                <Label className="text-xs text-muted-foreground">Timeline de eventos (SendGrid webhook)</Label>
                {events.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-2">
                    Nenhum evento recebido ainda. Eventos aparecem conforme o SendGrid
                    notifica entrega, abertura, clique, etc. (pode levar minutos).
                  </p>
                ) : (
                  <ol className="mt-3 space-y-2">
                    {events.map((ev: any) => (
                      <li
                        key={ev.id}
                        className="flex items-start gap-3 text-sm border-l-2 border-primary/30 pl-3 py-1"
                      >
                        <Badge variant="secondary" className="text-xs shrink-0 mt-0.5">
                          {eventLabel[ev.eventType] ?? ev.eventType}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleString("pt-BR") : "—"}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
